import React, { useState, useEffect, useRef } from "react";
import "./ProgressReport.css";
import "../SearchContainer.css";

import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Button,
  DialogActions,
  DialogContent,
  TableContainer,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import Pagination from "@mui/material/Pagination";
import CloseIcon from "@mui/icons-material/Close";
import logo from "../../../utilities/logo-png.png";
import { useTheme } from "../../../context/ThemeContext";

const ProgressRep = ({ progressReportsToggle }) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const [reportList, setReportList] = useState([]);
  const [totalReports, setTotalReports] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dialogContent, setDialogContent] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);

  // Search state
  const [searchName, setSearchName] = useState("");
  const [searchMode, setSearchMode] = useState("name"); // 'name' | 'date'
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [activeDateRange, setActiveDateRange] = useState({ from: "", to: "" });

  // View toggle
  const [viewMode, setViewMode] = useState("progress"); // 'progress' | 'hours'
  const [totalHoursData, setTotalHoursData] = useState([]);
  const [hoursLoading, setHoursLoading] = useState(false);

  // Total Hours sort + filter
  const [sortBy, setSortBy] = useState("hours-desc");
  const [overtimeOnly, setOvertimeOnly] = useState(false);

  const isFirstRender = useRef(true);

  // ── Core fetch ────────────────────────────────────────────────────────────
  const fetchReports = async (name, from, to, page) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (name) params.append("name", name);
    if (from) params.append("fromDate", from);
    if (to) params.append("toDate", to);
    params.append("page", page);
    try {
      const response = await fetch(`https://hr-backend-72v1.onrender.com/reportshow?${params}`);
      const data = await response.json();
      setReportList(data.reports);
      setTotalReports(data.totalReports);
      setTotalPages(Math.ceil(data.totalReports / 10));
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchReports("", "", "", 1);
  }, []);

  // Live name search - debounced 300ms, skip first render
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchReports(searchName, activeDateRange.from, activeDateRange.to, 1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchName]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    fetchReports(searchName, activeDateRange.from, activeDateRange.to, page);
  };

  const handleDropdownChange = (event) => {
    const page = event.target.value;
    setCurrentPage(page);
    fetchReports(searchName, activeDateRange.from, activeDateRange.to, page);
  };

  const handleDateSearch = () => {
    setActiveDateRange({ from: fromDate, to: toDate });
    setCurrentPage(1);
    fetchReports(searchName, fromDate, toDate, 1);
  };

  const handleDateClear = () => {
    setFromDate("");
    setToDate("");
    setActiveDateRange({ from: "", to: "" });
    setCurrentPage(1);
    fetchReports(searchName, "", "", 1);
  };

  const handleOpenDialog = (content) => {
    setDialogContent(content);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => setDialogOpen(false);

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (mode === "hours") fetchTotalHours();
  };

  // ── Total Hours ───────────────────────────────────────────────────────────
  const fetchTotalHours = async () => {
    setHoursLoading(true);
    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/gethrTotalHours");
      const data = await response.json();
      // Endpoint returns { [email]: { totalHours, overtimeHours } }
      const transformed = Object.entries(data)
        .map(([email, v]) => ({
          email,
          name: (v && v.name) || "",
          totalHours: (v && v.totalHours) || 0,
          overtimeHours: (v && v.overtimeHours) || 0,
        }))
        .sort((a, b) => b.totalHours - a.totalHours);
      setTotalHoursData(transformed);
    } catch (error) {
      console.error("Error fetching total hours:", error);
      setTotalHoursData([
        { email: "john@example.com", totalHours: 160, overtimeHours: 0 },
        { email: "jane@example.com", totalHours: 145, overtimeHours: 0 },
        { email: "mike@example.com", totalHours: 180, overtimeHours: 0 },
      ]);
    } finally {
      setHoursLoading(false);
    }
  };

  // ── MUI TextField sx for both themes ─────────────────────────────────────
  const muiInputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDark ? "#2a2a2a" : "#ffffff",
      "& fieldset": { borderColor: isDark ? "#555" : "#d1d5db" },
      "&:hover fieldset": { borderColor: isDark ? "#888" : "#3b82f6" },
      "&.Mui-focused fieldset": { borderColor: isDark ? "#d16f23" : "#3b82f6" },
    },
    "& .MuiInputBase-input": { color: isDark ? "#ffffff" : "#1a1a1a" },
    "& .MuiInputBase-input::placeholder": { color: isDark ? "#777" : "#9ca3af", opacity: 1 },
    "& .MuiInputLabel-root": { color: isDark ? "#9ca3af" : "#6b7280" },
    "& .MuiInputLabel-root.Mui-focused": { color: isDark ? "#d16f23" : "#3b82f6" },
  };

  // Total Hours sort <Select> styling (theme-aware) + dark dropdown menu.
  const thSelectSx = {
    minWidth: 250,
    backgroundColor: isDark ? "#2a2a2a" : "#ffffff",
    color: isDark ? "#ffffff" : "#1a1a1a",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: isDark ? "#555" : "#d1d5db" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: isDark ? "#888" : "#3b82f6" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: isDark ? "#d16f23" : "#3b82f6" },
    "& .MuiSvgIcon-root": { color: isDark ? "#bbb" : "#555" },
  };
  const thMenuProps = {
    PaperProps: {
      sx: {
        backgroundColor: isDark ? "#2a2a2a" : "#ffffff",
        color: isDark ? "#ffffff" : "#1a1a1a",
        "& .MuiMenuItem-root:hover": { backgroundColor: isDark ? "#3a3a3a" : "#f0f0f0" },
        "& .Mui-selected": { backgroundColor: isDark ? "#444" : "#e8e8e8" },
      },
    },
  };

  // Derived Total Hours list: overtime-only filter, then the chosen sort.
  const num = (v) => parseFloat(v) || 0;
  const displayedHours = (() => {
    let rows = [...totalHoursData];
    if (overtimeOnly) rows = rows.filter((e) => num(e.overtimeHours) > 0);
    switch (sortBy) {
      case "hours-asc":
        rows.sort((a, b) => num(a.totalHours) - num(b.totalHours));
        break;
      case "name-asc":
        rows.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
        break;
      case "name-desc":
        rows.sort((a, b) => (b.name || "").localeCompare(a.name || "", undefined, { sensitivity: "base" }));
        break;
      case "ot-desc":
        rows.sort((a, b) => num(b.overtimeHours) - num(a.overtimeHours));
        break;
      case "hours-desc":
      default:
        rows.sort((a, b) => num(b.totalHours) - num(a.totalHours));
        break;
    }
    return rows;
  })();

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>

      {/* ── View toggle ── */}
      <div className="prToggleGroup">
        <button
          className={`prToggleBtn ${viewMode === "progress" ? "prToggleBtn--active" : ""}`}
          onClick={() => handleViewModeChange("progress")}
        >
          Progress Reports
        </button>
        <button
          className={`prToggleBtn ${viewMode === "hours" ? "prToggleBtn--active" : ""}`}
          onClick={() => handleViewModeChange("hours")}
        >
          Total Hours
        </button>
      </div>

      {/* ── Progress Reports view ── */}
      {viewMode === "progress" && (
        <>
          {/* Search card */}
          <div
            className="search-container"
            style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
          >
            <div className="night">
              <div className="star"></div>
              <div className="star"></div>
              <div className="star"></div>
              <div className="star"></div>
              <div className="star"></div>
            </div>

            <div className="prSearchArea">
              {/* Search mode toggle */}
              <div className="prSearchModeGroup">
                <button
                  className={`prSearchModeBtn ${searchMode === "name" ? "prSearchModeBtn--active" : ""}`}
                  onClick={() => setSearchMode("name")}
                >
                  Search By Name
                </button>
                <button
                  className={`prSearchModeBtn ${searchMode === "date" ? "prSearchModeBtn--active" : ""}`}
                  onClick={() => setSearchMode("date")}
                >
                  Search By Date
                </button>
              </div>

              {/* Name input */}
              {searchMode === "name" && (
                <TextField
                  variant="outlined"
                  label="Search by name"
                  value={searchName}
                  onChange={e => setSearchName(e.target.value)}
                  placeholder="Type a name to filter results..."
                  fullWidth
                  size="small"
                  sx={muiInputSx}
                />
              )}

              {/* Date range inputs */}
              {searchMode === "date" && (
                <div className="prDateRow">
                  <div className="prDateField">
                    <TextField
                      variant="outlined"
                      label="From Date"
                      type="date"
                      value={fromDate}
                      onChange={e => setFromDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                      sx={muiInputSx}
                      inputProps={{ style: { colorScheme: isDark ? "dark" : "light" } }}
                    />
                  </div>
                  <div className="prDateField">
                    <TextField
                      variant="outlined"
                      label="To Date"
                      type="date"
                      value={toDate}
                      onChange={e => setToDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                      sx={muiInputSx}
                      inputProps={{ style: { colorScheme: isDark ? "dark" : "light" } }}
                    />
                  </div>
                  <div className="prDateBtns">
                    <button className="prSearchBtn" onClick={handleDateSearch}>Search</button>
                    <button className="prClearBtn" onClick={handleDateClear}>Clear</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Table */}
          <TableContainer component={Paper} className="leaveContainer">
            <h4 className="myTableHeader animate__animated animate__lightSpeedInLeft">
              PROGRESS REPORTS
            </h4>
            <div className="prResultCount">
              Showing {totalReports} result{totalReports !== 1 ? "s" : ""}
              {(searchName || activeDateRange.from || activeDateRange.to) && " (filtered)"}
            </div>

            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
                <CircularProgress />
              </div>
            ) : (
              <Table sx={{ minWidth: 650 }} className="leaveTable">
                <TableHead>
                  <TableRow>
                    <TableCell className="myTableHeadCells">
                      <span className="myTableHeadSpan">Name</span>
                    </TableCell>
                    <TableCell className="myTableHeadCells">
                      <span className="myTableHeadSpan">Email</span>
                    </TableCell>
                    <TableCell className="myTableHeadCells">
                      <span className="myTableHeadSpan">Date</span>
                    </TableCell>
                    <TableCell className="myTableHeadCells" align="right">
                      <span className="myTableHeadSpan">Report</span>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportList && reportList.length > 0 ? (
                    reportList.map((report) => {
                      const date = new Date(report.date);
                      const [datePart, timePart] = date.toISOString().split("T");
                      const [hourStr, minuteStr] = timePart.split(":");
                      let hour = parseInt(hourStr, 10);
                      const ampm = hour >= 12 ? "PM" : "AM";
                      hour = hour % 12 || 12;
                      const formattedTime = `${hour}:${minuteStr} ${ampm}`;

                      return (
                        <TableRow key={report._id}>
                          <TableCell sx={{ fontSize: "large" }}>{report.name}</TableCell>
                          <TableCell sx={{ fontSize: "large" }}>{report.email}</TableCell>
                          <TableCell sx={{ fontSize: "large", height: "50px" }}>
                            {datePart} ⏱&nbsp;{formattedTime}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: "large" }}>
                            <Button onClick={() => handleOpenDialog(report)}>View Report</Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <div className="prNoResults">No reports found for your search</div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}

            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "20px", margin: "20px 0" }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
              <Select
                value={currentPage}
                onChange={handleDropdownChange}
                sx={{ minWidth: 120 }}
                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
              >
                {Array.from({ length: totalPages }, (_, index) => (
                  <MenuItem key={index + 1} value={index + 1}>
                    Page {index + 1}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </TableContainer>
        </>
      )}

      {/* ── Total Hours view ── */}
      {viewMode === "hours" && (
        <TableContainer component={Paper} className="leaveContainer">
          <h4 className="myTableHeader animate__animated animate__lightSpeedInLeft">
            TOTAL HOURS
          </h4>

          {/* Sort + overtime filter controls */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "14px",
              alignItems: "center",
              justifyContent: "center",
              margin: "4px 20px 0",
            }}
          >
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              size="small"
              sx={thSelectSx}
              MenuProps={thMenuProps}
            >
              <MenuItem value="hours-desc">Total Hours (High → Low)</MenuItem>
              <MenuItem value="hours-asc">Total Hours (Low → High)</MenuItem>
              <MenuItem value="name-asc">Name (A → Z)</MenuItem>
              <MenuItem value="name-desc">Name (Z → A)</MenuItem>
              <MenuItem value="ot-desc">Approved Overtime (High → Low)</MenuItem>
            </Select>
            <button
              className={`prToggleBtn ${overtimeOnly ? "prToggleBtn--active" : ""}`}
              onClick={() => setOvertimeOnly((v) => !v)}
            >
              {overtimeOnly ? "✓ " : ""}Only Approved Overtime
            </button>
          </div>
          <div className="prResultCount" style={{ textAlign: "center" }}>
            Showing {displayedHours.length} of {totalHoursData.length} employee
            {totalHoursData.length !== 1 ? "s" : ""}
            {overtimeOnly ? " (approved overtime only)" : ""}
          </div>

          {hoursLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <Table sx={{ minWidth: 650 }} className="leaveTable">
                <TableHead>
                  <TableRow>
                    <TableCell className="myTableHeadCells">
                      <span className="myTableHeadSpan">Name</span>
                    </TableCell>
                    <TableCell className="myTableHeadCells">
                      <span className="myTableHeadSpan">Email</span>
                    </TableCell>
                    <TableCell className="myTableHeadCells">
                      <span className="myTableHeadSpan">Total Hours</span>
                    </TableCell>
                    <TableCell className="myTableHeadCells">
                      <span className="myTableHeadSpan">Approved Overtime</span>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedHours.length > 0 ? (
                    displayedHours.map((employee, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontSize: "large" }}>{employee.name || "—"}</TableCell>
                        <TableCell sx={{ fontSize: "large" }}>{employee.email}</TableCell>
                        <TableCell sx={{ fontSize: "large", fontWeight: "bold" }}>
                          {employee.totalHours} hrs
                        </TableCell>
                        <TableCell sx={{ fontSize: "large" }}>
                          {employee.overtimeHours || 0} hrs
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <div className="prNoResults">
                          {overtimeOnly
                            ? "No employees with approved overtime this month"
                            : "No data"}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <div style={{
                margin: "20px",
                padding: "15px",
                backgroundColor: isDark ? "#333333" : "#f5f5f5",
                borderRadius: "8px",
                border: isDark ? "1px solid #555" : "1px solid #ddd",
              }}>
                <h5 style={{ margin: "0 0 10px 0", color: isDark ? "#ffffff" : "#333" }}>Summary:</h5>
                <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                  <div><strong>Total Employees:</strong> {displayedHours.length}</div>
                  <div>
                    <strong>Average Hours:</strong>{" "}
                    {displayedHours.length > 0
                      ? (displayedHours.reduce((sum, e) => sum + parseFloat(e.totalHours), 0) / displayedHours.length).toFixed(2)
                      : 0}{" "}hrs
                  </div>
                  <div>
                    <strong>Complete (160+ hrs):</strong>{" "}
                    {displayedHours.filter(e => parseFloat(e.totalHours) >= 160).length}
                  </div>
                  <div>
                    <strong>Total Approved Overtime:</strong>{" "}
                    {displayedHours.reduce((sum, e) => sum + (parseFloat(e.overtimeHours) || 0), 0).toFixed(2)}{" "}hrs
                  </div>
                </div>
              </div>
            </>
          )}
        </TableContainer>
      )}

      {/* ── Report dialog ── */}
      {dialogOpen && (
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogActions sx={{ position: "absolute", right: 0, padding: "15px 5px" }}>
            <Button onClick={handleCloseDialog}><CloseIcon /></Button>
          </DialogActions>
          <div className="dialogTop">
            <div className="dialogLeft">
              <DialogTitle className="dialogHeading">Name</DialogTitle>
              <p className="dialogtext">{dialogContent.name}</p>
              <DialogTitle className="dialogHeading">Email</DialogTitle>
              <p className="dialogtext">{dialogContent.email}</p>
              <DialogTitle className="dialogHeading">Date</DialogTitle>
              <p className="dialogtext">
                {new Date(dialogContent.date).toISOString().split("T")[0]} ⏱&nbsp;
                {new Date(dialogContent.date).toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
              </p>
            </div>
            <div className="dialogRight">
              <img src={logo} width="220" height="220" alt="GROWTH GUILD" />
            </div>
          </div>
          <DialogTitle className="dialogHeading">Report</DialogTitle>
          <DialogContent className="dialogtextReport">
            {dialogContent.reportDetail}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ProgressRep;
